/*
 * ticket-bundle-gate.js — チケット単独購入の抑止（対応リスト No.59）
 *
 * 要件:
 *   チケット商品は「対象商品と同時購入する場合のみ」購入可能。
 *   チケット単体ではカートに入れられず、チェックアウトもできない。
 *
 * 判定の正:
 *   判定は必ず Liquid（snippets/ticket-bundle-gate.liquid）が行う。
 *   Liquid は item.product.collections で商品側からコレクションを引くため、
 *   非公開商品やコレクションの件数上限の影響を受けない。
 *   カートが変わったときは /cart を取得して、そこに描画されたゲートの
 *   data 属性を読み直す。cart.js には collection 情報が無く、
 *   handle 一覧での照合では非公開商品や51件目以降を取りこぼすため。
 *
 * 抑止の二層構成:
 *   1. 見た目 — 専用クラスによる不透明化と非表示。
 *      disabled 属性には触らない。テーマが売り切れバリアント等で付け外しするため、
 *      取り合いになって互いの状態を壊す。
 *   2. イベント — capture フェーズの submit / click / keydown で実行そのものを止める。
 *      こちらが本来の防波堤。
 *
 *   決済ボタン(Shop Pay / Apple Pay / Google Pay / 加速決済)は disabled が効かない。
 *   .shopify-payment-button はコンテナで実ボタンは内側、加速決済は iframe のことも
 *   あるため、これらは「非表示」にする。
 *
 * 範囲外:
 *   チェックアウト（/checkout 直アクセス、カートパーマリンク経由）は Shopify ホスト側で
 *   動作するため、本スクリプトでは阻止できない。サーバー側で完全に阻止する場合は
 *   Cart and Checkout Validation Function が必要。
 */
(function () {
  "use strict";

  var GATE_SELECTOR = "[data-ticket-bundle-gate]";
  var BLOCK_CLASS = "ticket-bundle-gate--block";
  var DISABLED_CLASS = "ticket-bundle-gate--disabled";
  var HIDDEN_CLASS = "ticket-bundle-gate--hidden";
  var NOTICE_CLASS = "ticket-bundle-gate__notice";

  // チケット商品の購入フォームであることの印（イベント層が参照する）
  var TICKET_FORM_ATTR = "data-ticket-bundle-gate-form";

  var PRODUCT_FORM_SELECTOR = 'form[action*="/cart/add"]';

  // 決済ボタン類。disabled が効かないため非表示で抑止する。
  var PAYMENT_BUTTON_SELECTOR = [
    ".shopify-payment-button",
    '[data-shopify="payment-button"]',
    ".additional-checkout-buttons",
    "[data-shopify-buttoncontainer]",
  ].join(",");

  // カート内容でチェックアウトへ進む通常のボタン・リンク
  var CART_CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    '[name="checkout"]',
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]',
  ].join(",");

  var REFRESH_DEBOUNCE_MS = 80;
  var MAX_RETRY = 3;
  var refreshTimer = null;
  var requestSeq = 0;
  var retryCount = 0;

  // イベント層が参照する現在の抑止状態。
  var state = { hasTicket: false, hasEligible: false, ready: false };

  function gates() {
    return Array.prototype.slice.call(document.querySelectorAll(GATE_SELECTOR));
  }

  function attr(el, name) {
    return el.getAttribute(name) === "true";
  }

  function activeGates() {
    return gates().filter(function (g) {
      return attr(g, "data-gate-active");
    });
  }

  /** ゲート要素に紐づく購入フォーム。ゲートはフォームの直前の兄弟に置かれる前提。 */
  function productFormFor(gate) {
    var el = gate.nextElementSibling;
    while (el) {
      if (el.matches && el.matches(PRODUCT_FORM_SELECTOR)) return el;
      if (el.querySelector) {
        var nested = el.querySelector(PRODUCT_FORM_SELECTOR);
        if (nested) return nested;
      }
      el = el.nextElementSibling;
    }
    return gate.parentElement
      ? gate.parentElement.querySelector(PRODUCT_FORM_SELECTOR)
      : null;
  }

  /**
   * カート状態を取得する。/cart を描画させてゲートの data 属性を読む。
   * カートが空だとテーマがゲートを描画しないため、200応答でゲートが無い場合のみ
   * 空カートとみなす。HTTPエラーは throw して呼び出し側で「前の状態を維持」させる
   * （エラーで抑止が解除されると単独購入が通ってしまうため）。
   */
  function fetchCartState() {
    return fetch("/cart", {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
    })
      .then(function (r) {
        if (!r.ok) throw new Error("cart fetch failed: " + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var remote = doc.querySelector(GATE_SELECTOR);
        if (!remote) return { hasTicket: false, hasEligible: false };
        return {
          hasTicket: attr(remote, "data-cart-has-ticket"),
          hasEligible: attr(remote, "data-cart-has-eligible"),
        };
      });
  }

  /**
   * 見た目上の抑止。disabled 属性には触らない。
   * テーマが売り切れバリアント等で disabled を付け外しするため、
   * こちらが解除するときに相手の disabled まで消してしまう。
   * 実際の抑止はイベント層が担うので、ここは表示だけで足りる。
   */
  function setBlockedVisual(el, blocked) {
    var had = el.classList.contains(DISABLED_CLASS);
    el.classList.toggle(DISABLED_CLASS, blocked);
    if (blocked) {
      el.setAttribute("aria-disabled", "true");
    } else if (had) {
      el.removeAttribute("aria-disabled");
    }
  }

  /** 非表示による抑止。決済ボタン専用。 */
  function setHidden(el, hidden) {
    el.classList.toggle(HIDDEN_CLASS, hidden);
  }

  /**
   * 案内文を1つだけ表示する。
   * Liquid が描画済みの案内があればそれを再利用する。JS専用の要素を別に足すと
   * 同じ文言が二重表示になるため、コンテナ内の案内は常に1つに保つ。
   */
  function ensureNotice(gate, show, message, modifier) {
    var existing = gate.querySelector("." + NOTICE_CLASS);

    if (!show) {
      if (existing) existing.remove();
      return;
    }
    if (existing) {
      existing.textContent = message;
      return;
    }

    var p = document.createElement("p");
    p.className = NOTICE_CLASS + (modifier ? " " + modifier : "");
    p.setAttribute("role", "alert");
    p.textContent = message;
    gate.appendChild(p);
  }

  /** CSS の保険が参照する data 属性を、判定結果に追従させる */
  function syncAttributes(gate) {
    gate.setAttribute("data-cart-has-ticket", String(state.hasTicket));
    gate.setAttribute("data-cart-has-eligible", String(state.hasEligible));
    gate.setAttribute("data-condition-met", String(state.hasEligible));
  }

  /** 商品ページ: 表示中の商品がチケットなら購入導線を抑止する */
  function applyProductPage(gate) {
    if (!attr(gate, "data-current-is-ticket")) return;

    var form = productFormFor(gate);
    if (!form) return;

    form.setAttribute(TICKET_FORM_ATTR, "");

    var blocked = productBlocked();
    form.classList.toggle(BLOCK_CLASS, blocked);

    var submit = form.querySelector('[type="submit"]');
    if (submit) setBlockedVisual(submit, blocked);

    // 「今すぐ購入」はカート内容を含めず、この商品だけで決済する。
    // 対象商品がカートにあってもチケット単体購入が成立するため常に非表示。
    form.querySelectorAll(PAYMENT_BUTTON_SELECTOR).forEach(function (el) {
      setHidden(el, true);
    });

    ensureNotice(gate, blocked, gate.getAttribute("data-notice-product") || "");
  }

  /** カート: チケットが入っているのに対象商品が無ければチェックアウトを止める */
  function applyCart(gate) {
    var blocked = cartBlocked();

    document.querySelectorAll(CART_CHECKOUT_SELECTOR).forEach(function (el) {
      if (el.closest(PRODUCT_FORM_SELECTOR)) return;
      setBlockedVisual(el, blocked);
    });

    // 加速決済は disabled が効かないので非表示にする。
    // 商品ページの「今すぐ購入」は applyProductPage が担当するので除外。
    document.querySelectorAll(PAYMENT_BUTTON_SELECTOR).forEach(function (el) {
      if (el.closest(PRODUCT_FORM_SELECTOR)) return;
      setHidden(el, blocked);
    });

    if (gate.getAttribute("data-context") !== "product") {
      ensureNotice(
        gate,
        blocked,
        gate.getAttribute("data-notice-cart") || "",
        "ticket-bundle-gate__notice--cart"
      );
    }
  }

  function apply() {
    activeGates().forEach(function (gate) {
      syncAttributes(gate);
      applyProductPage(gate);
      applyCart(gate);
    });
  }

  function refresh() {
    var active = activeGates();
    if (!active.length) return;

    if (!state.ready) {
      // 初回は Liquid の描画時点の値を採用する（ちらつき防止）
      state.hasTicket = attr(active[0], "data-cart-has-ticket");
      state.hasEligible = attr(active[0], "data-cart-has-eligible");
      state.ready = true;
      apply();
    }

    var mySeq = ++requestSeq;
    fetchCartState()
      .then(function (next) {
        if (mySeq !== requestSeq) return; // 古い応答は捨てる
        retryCount = 0;
        state.hasTicket = next.hasTicket;
        state.hasEligible = next.hasEligible;
        apply();
      })
      .catch(function () {
        // 通信失敗時は直前の状態（＝抑止側）を維持する。
        // 空カート扱いにすると抑止が解除されてしまうため。
        //
        // ただし維持したままだと、カート変更前に倒した hasEligible=false から
        // 復帰できずリロードするまで購入できなくなる。イベント層が再試行の
        // submit も止めるため自力で戻れないので、数回だけ再取得を試みる。
        if (mySeq !== requestSeq) return;
        if (retryCount >= MAX_RETRY) return;
        retryCount += 1;
        setTimeout(refresh, 1000 * retryCount);
      });
  }

  /**
   * カートが変わったので再取得する。
   * 再取得が終わるまでは「対象商品なし」に倒しておく。
   * 対象商品をAjax削除した直後はサーバ上チケット単独になっているのに、
   * 古い hasEligible=true のままだとその窓でチェックアウトが通ってしまう。
   * チケットが無いカート(hasTicket=false)では cartBlocked() が false のままなので、
   * 通常の買い物には影響しない。
   */
  function markCartPending() {
    state.hasEligible = false;
    if (state.ready) apply();
  }

  function scheduleRefresh() {
    markCartPending();
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, REFRESH_DEBOUNCE_MS);
  }

  // ---- イベント層（本来の防波堤）----
  // disabled 属性は他スクリプトに書き換えられるため、実行そのものを capture で止める。

  function productBlocked() {
    return !state.hasEligible;
  }

  function cartBlocked() {
    return state.hasTicket && !state.hasEligible;
  }

  document.addEventListener(
    "submit",
    function (e) {
      var form = e.target;
      if (!form || !form.hasAttribute) return;

      // チケット商品の「カートに追加」
      if (form.hasAttribute(TICKET_FORM_ATTR) && productBlocked()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      // カートフォームからのチェックアウト送信
      var submitter = e.submitter;
      if (submitter && submitter.name === "checkout" && cartBlocked()) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );

  function blockCheckoutInteraction(e) {
    if (!cartBlocked()) return;
    var target = e.target;
    if (!target || !target.closest) return;

    var hit = target.closest(
      CART_CHECKOUT_SELECTOR + "," + PAYMENT_BUTTON_SELECTOR
    );
    if (!hit) return;
    if (hit.closest(PRODUCT_FORM_SELECTOR)) return;

    e.preventDefault();
    e.stopImmediatePropagation();
  }

  document.addEventListener("click", blockCheckoutInteraction, true);
  document.addEventListener(
    "keydown",
    function (e) {
      if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
      blockCheckoutInteraction(e);
    },
    true
  );

  // ---- 起動と再判定 ----

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refresh);
  } else {
    refresh();
  }

  ["cart:updated", "cart:refresh", "variant:add"].forEach(function (name) {
    document.addEventListener(name, scheduleRefresh);
  });

  // 上記イベントを発火しないテーマ向けに、カート系リクエストも直接フックする。
  // 両方が発火しても scheduleRefresh のデバウンスで1回にまとまる。
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function () {
      var url =
        typeof arguments[0] === "string"
          ? arguments[0]
          : (arguments[0] && arguments[0].url) || "";

      var isCartMutation = /\/cart\/(add|change|update|clear)/.test(url);

      // 送信前に抑止側へ倒しておく。応答を待つ間に窓が開かないようにするため。
      if (isCartMutation) markCartPending();

      var promise = origFetch.apply(this, arguments);

      // 失敗時も再取得する。抑止側に倒したまま復帰できなくなるのを防ぐ。
      if (isCartMutation) promise.then(scheduleRefresh, scheduleRefresh);

      return promise;
    };
  }
})();
